import { Box, Button, Modal, Select, TextArea, TextBlock } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./ai-feedback-modal.scss";
import { Breakpoint } from "domain/breakpoints";

import testIds from "testIds.json";

interface Props {
    feedback: "positive" | "negative";
    isOpen: boolean;
    hideModal: () => void;
    onSubmit: (reason: string | undefined, details: string) => void;
}

export const AIFeedbackModal = (props: Props) => {
    const { t } = useTranslation();
    const [selectedReason, setSelectedReason] = React.useState<string | undefined>(undefined);
    const [details, setDetails] = React.useState("");
    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= Breakpoint.MD);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${Breakpoint.MD}px)`);
        const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    const handleHideModal = () => {
        setSelectedReason(undefined);
        setDetails("");
        props.hideModal();
    };

    const handleSubmit = () => {
        if (props.feedback === "negative" && selectedReason === undefined) {
            return;
        }
        props.onSubmit(selectedReason, details);
        setSelectedReason(undefined);
        setDetails("");
    };

    const reasonItems = [
        t("AiAgent.feedback.reason.notFollowingMyRequest"),
        t("AiAgent.feedback.reason.incorrectInformation"),
        t("AiAgent.feedback.reason.notHelpful"),
        t("AiAgent.feedback.reason.incompleteResponse"),
        t("AiAgent.feedback.reason.technicalIssue"),
        t("AiAgent.feedback.reason.safetyOrLegalConcern"),
        t("Common.other"),
    ];

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={handleHideModal}
            title={t("AiAgent.feedback.title")}
            testId={testIds.workArea.aiPanel.feedbackModal.itself}
            width="Md"
            hideCloseIcon
            buttons={
                <>
                    <Button
                        variant="Primary"
                        onClick={handleSubmit}
                        disabled={props.feedback === "negative" && selectedReason === undefined}
                        testId={testIds.workArea.aiPanel.feedbackModal.submitButton}
                    >
                        {t("AiAgent.feedback.submitButton")}
                    </Button>
                    <Button variant="Secondary" onClick={handleHideModal} testId={testIds.common.dialog.closeButton}>
                        {t("Common.cancel")}
                    </Button>
                </>
            }
        >
            <Box columnLayout gap="Md">
                {props.feedback === "negative" && (
                    <>
                        <TextBlock disableBottomSpacing>{t("AiAgent.feedback.description")}</TextBlock>
                        <Select
                            name="feedbackReason"
                            label={t("AiAgent.feedback.reason.label")}
                            placeholder={t("Common.select")}
                            items={reasonItems}
                            onSelection={(value) => setSelectedReason(String(value))}
                            closeOnSelection
                            labelAbove={isMobile}
                            testId={testIds.workArea.aiPanel.feedbackModal.reasonSelect}
                        />
                    </>
                )}

                {/* TODO BCC-6714: Remove CSS class for width when the textarea size is fixed in CL. */}
                <div className={style.textarea}>
                    <TextArea
                        name="feedbackDetails"
                        label={t("AiAgent.feedback.detailsLabel")}
                        optionalLabel={t("Common.optional")}
                        hideLabelSection={false}
                        value={details}
                        onChange={(event) => setDetails(event.target.value)}
                        labelAbove={isMobile}
                        testIds={{ textArea: testIds.workArea.aiPanel.feedbackModal.detailsInput }}
                    />
                </div>
            </Box>
        </Modal>
    );
};
